import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LangContext';

const API = process.env.REACT_APP_API_URL || '';

export default function Login() {
  const { login, signInWithGoogle } = useAuth();
  const { lang, dir } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/app';

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError]       = useState('');

  const L = {
    title:     lang === 'ar' ? 'تسجيل الدخول' : lang === 'fa' ? 'ورود به حساب' : 'Sign In',
    email:     lang === 'ar' ? 'البريد الإلكتروني' : lang === 'fa' ? 'ایمیل' : 'Email',
    password:  lang === 'ar' ? 'كلمة المرور' : lang === 'fa' ? 'رمز عبور' : 'Password',
    submit:    lang === 'ar' ? 'دخول' : lang === 'fa' ? 'ورود' : 'Sign in',
    loading:   lang === 'ar' ? 'جارٍ التحقق...' : lang === 'fa' ? 'در حال بررسی...' : 'Signing in…',
    orWith:    lang === 'ar' ? 'أو' : lang === 'fa' ? 'یا' : 'or',
    google:    lang === 'ar' ? 'المتابعة مع Google' : lang === 'fa' ? 'ورود با گوگل' : 'Continue with Google',
    googleLoading: lang === 'ar' ? 'جارٍ الاتصال...' : lang === 'fa' ? 'در حال اتصال...' : 'Connecting…',
    noAccount: lang === 'ar' ? 'ليس لديك حساب؟' : lang === 'fa' ? 'حساب ندارید؟' : "Don't have an account?",
    signUp:    lang === 'ar' ? 'إنشاء حساب' : lang === 'fa' ? 'ثبت‌نام' : 'Sign up',
    backToApp: lang === 'ar' ? '← العودة للتطبيق' : lang === 'fa' ? '← بازگشت به برنامه' : '← Back to app',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
      login(data.access_token, data.user);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || (lang === 'ar' ? 'بيانات غير صحيحة' : lang === 'fa' ? 'اطلاعات نادرست' : 'Invalid email or password'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      await signInWithGoogle(lang);
      navigate(from, { replace: true });
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user') {
        // user dismissed — no error shown
      } else {
        setError(err.response?.data?.detail || err.message || (lang === 'ar' ? 'فشل تسجيل الدخول عبر Google' : lang === 'fa' ? 'ورود با گوگل ناموفق بود' : 'Google sign-in failed'));
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '10px', padding: '11px 14px',
    color: '#f0f0f5', fontSize: '14px', fontFamily: 'inherit', outline: 'none',
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(160deg, #04051a 0%, #070920 55%, #0a0d32 100%)',
      padding: '24px', direction: dir,
    }}>
      <div style={{
        width: '100%', maxWidth: '400px',
        background: 'rgba(10, 12, 32, 0.90)',
        backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '20px', padding: '36px 32px',
        boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
      }}>
        <div style={{ marginBottom: '28px' }}>
          <Link to="/app" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', textDecoration: 'none', display: 'inline-block', marginBottom: '20px' }}>
            {L.backToApp}
          </Link>
          <h1 style={{ fontSize: '22px', fontWeight: 800, color: '#f0f0f5', margin: 0 }}>{L.title}</h1>
        </div>

        {/* Google button */}
        <button
          onClick={handleGoogle}
          disabled={googleLoading || loading}
          style={{
            width: '100%', padding: '11px 14px', marginBottom: '20px',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '10px', color: '#f0f0f5',
            fontFamily: 'inherit', fontSize: '14px', fontWeight: 600,
            cursor: googleLoading ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
            transition: 'background 0.2s',
          }}
          onMouseEnter={e => { if (!googleLoading) e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
        >
          {googleLoading ? (
            <><span className="spinner" />{L.googleLoading}</>
          ) : (
            <>
              <GoogleIcon />
              {L.google}
            </>
          )}
        </button>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.1)' }} />
          <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: '12px' }}>{L.orWith}</span>
          <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.1)' }} />
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: '6px' }}>{L.email}</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus style={inputStyle} />
          </div>

          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: '6px' }}>{L.password}</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required style={inputStyle} />
          </div>

          {error && (
            <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#f87171' }}>
              {error}
            </div>
          )}

          <button type="submit" disabled={loading || googleLoading} style={{
            marginTop: '4px', padding: '12px',
            background: 'rgba(78,154,241,0.2)', border: '1.5px solid rgba(78,154,241,0.5)',
            borderRadius: '10px', color: '#4e9af1', fontWeight: 700, fontSize: '14px',
            cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          }}>
            {loading ? <><span className="spinner" />{L.loading}</> : L.submit}
          </button>
        </form>

        <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '13px', color: 'rgba(255,255,255,0.4)' }}>
          {L.noAccount}{' '}
          <Link to="/register" state={{ from }} style={{ color: '#4e9af1', fontWeight: 600, textDecoration: 'none' }}>
            {L.signUp}
          </Link>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}
